import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * <collection-builder> — sections/collection-builder.liquid
 *
 * A merchandising interface over the LIVE Shopify cart. There is no builder state to "add":
 * every + and − mutates the real cart. The element holds no truth of its own — it holds an
 * OPTIMISTIC projection that is continuously reconciled against the cart.
 *
 * ## Three pieces of state, deliberately separate
 *
 *  `#desired` — what the shopper has asked for. Drives the UI, updates synchronously on click
 *               so the interface never feels like it is waiting on the network.
 *  `#lines`   — server truth: variantId → { key, quantity } for ONE-TIME lines only.
 *               Subscription lines are invisible here; a Subscribe & Save line belongs to
 *               Recharge and must not be editable from the builder.
 *  `#dirty`   — variants whose desired quantity has not yet been written to the cart.
 *
 * ## Why a coalescing queue rather than one request per click
 *
 * Shopify's cart AJAX API has no concurrency control — two in-flight mutations race and the
 * loser silently wins. Tapping + five times must not produce five racing requests. So clicks
 * only mark a variant dirty; a single serialized worker drains the set, always writing the
 * variant's CURRENT desired quantity (an absolute value, so a coalesced write is identical to
 * the sequence it replaces). Five taps become one request.
 *
 * After the queue drains we re-read `/cart.js` once and reconcile — one authoritative read per
 * burst rather than per click — then announce a single `cart:update` so the drawer, the cart
 * icon and the PDP savings bar all refresh themselves.
 *
 * ## The drawer must NOT open
 *
 * `cart-drawer.js` opens on any `cart:update` when it has `auto-open`. A builder that popped the
 * drawer on every + would be unusable, so our event carries `data.silent` and the drawer skips
 * auto-open for it. That flag is generic — any surface that edits the cart in place can use it.
 */

const CART_JSON_URL = `${Theme?.routes?.cart_url ?? '/cart'}.js`;

class CollectionBuilder extends HTMLElement {
  #controller = new AbortController();
  /** @type {Map<string, HTMLElement>} */
  #cards = new Map();
  /** @type {Map<string, number>} */
  #desired = new Map();
  /** @type {Map<string, {key: string, quantity: number}>} */
  #lines = new Map();
  /** @type {Set<string>} */
  #dirty = new Set();
  #draining = false;
  #syncedKeys = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.max = Number(this.dataset.max) || 0;
    this.unitLabel = this.dataset.unitLabel || '';
    this.sectionId = this.dataset.sectionId || 'collection-builder';

    this.tray = this.querySelector('[data-cb-tray]');
    this.bar = this.querySelector('[data-cb-bar]');
    this.countEl = this.querySelector('[data-cb-count]');
    this.notice = this.querySelector('[data-cb-notice]');
    this.status = this.querySelector('[data-cb-status]');

    // Seed from the server render — correct on first paint, no flash, no boot request.
    for (const card of this.querySelectorAll('[data-cb-card]')) {
      const id = card.dataset.variantId;
      if (!id) continue;
      this.#cards.set(id, /** @type {HTMLElement} */ (card));
      const qty = Number(card.dataset.quantity) || 0;
      if (qty > 0) this.#desired.set(id, qty);
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('[data-cb-checkout]')?.addEventListener('click', this.#onViewBag, { signal });

    // Another surface (drawer, PDP, quick-add) changed the cart — we are not the authority,
    // so adopt whatever it says. Ignore our own events to avoid a feedback loop.
    document.addEventListener(
      'cart:update',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (detail?.data?.source === 'collection-builder') return;
        this.#reconcile(detail?.resource);
      },
      { signal }
    );

    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #total() {
    let total = 0;
    for (const qty of this.#desired.values()) total += qty;
    return total;
  }

  /**
   * The unit label, singularised at a count of one — the metafield stores the plural ("Bars"),
   * so "1 Bars In Your Bag" is the failure mode this exists to prevent. Same naive trailing-s
   * rule the savings bar uses, kept deliberately simple; an irregular plural would need the
   * merchant to supply both forms.
   */
  #unitFor(total) {
    const unit = this.unitLabel;
    if (!unit) return '';
    if (total === 1 && unit.toLowerCase().endsWith('s')) return unit.slice(0, -1);
    return unit;
  }

  /** Hard ceiling for one card: its own stock limit, and whatever headroom the builder max leaves. */
  #ceilingFor(id) {
    const card = this.#cards.get(id);
    const stock = Number(card?.dataset.maxStock ?? -1);
    const current = this.#desired.get(id) || 0;
    const headroom = this.max > 0 ? Math.max(0, this.max - (this.#total - current)) : Infinity;
    const byStock = stock >= 0 ? stock : Infinity;
    return Math.min(byStock, headroom);
  }

  /**
   * "View bag" opens the cart DRAWER rather than navigating to /cart — leaving the builder is the
   * opposite of what this interface is for.
   *
   * It clicks the header's own drawer trigger instead of calling `showDialog()` directly, so the
   * native open path runs intact (focus handling, sticky-summary state, mobile history entry). The
   * theme's declarative `on:click="cart-drawer-component/open"` is not an option here: that
   * resolves via `element.closest(selector)`, and this button is not inside the drawer.
   *
   * The anchor keeps its real `href`, so with no JS — or no drawer on the page — it still works.
   */
  #onViewBag = (event) => {
    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (!(trigger instanceof HTMLElement)) return; // fall through to the href
    event.preventDefault();
    trigger.click();
  };

  #onClick = (event) => {
    const step = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-cb-step]');
    if (!(step instanceof HTMLElement) || step.hasAttribute('disabled')) return;

    const card = step.closest('[data-cb-card]');
    if (!(card instanceof HTMLElement) || card.dataset.available !== 'true') return;

    const id = card.dataset.variantId;
    if (!id) return;

    const delta = Number(step.dataset.cbStep);
    const next = Math.max(0, (this.#desired.get(id) || 0) + delta);
    if (next > this.#ceilingFor(id)) return;

    if (next === 0) this.#desired.delete(id);
    else this.#desired.set(id, next);

    this.#dirty.add(id);
    this.#clearNotice();
    this.#render();
    void this.#drain();
  };

  // ---------------------------------------------------------------------------
  // Cart writes
  // ---------------------------------------------------------------------------

  /**
   * Line keys are the only safe identifier for a change: `/cart/change.js` addressed by variant
   * id is ambiguous when a variant sits on more than one line (e.g. a one-time line AND a
   * subscription line), and addressed by index it breaks the moment lines shift. We read them
   * once, lazily — a builder opened on an empty cart never pays for this.
   */
  async #ensureLineKeys() {
    if (this.#syncedKeys) return;
    this.#syncedKeys = true;
    try {
      const response = await fetch(CART_JSON_URL);
      if (response.ok) this.#adoptLines(await response.json());
    } catch {
      // Non-fatal: an add-then-change on a fresh line still works, we just lose the fast path.
    }
  }

  /** Rebuild server truth from a cart payload. One-time lines only. */
  #adoptLines(cart) {
    if (!cart?.items) return;
    this.#lines.clear();
    for (const item of cart.items) {
      if (item.selling_plan_allocation) continue;
      const id = String(item.variant_id);
      if (!this.#cards.has(id)) continue;
      const existing = this.#lines.get(id);
      // Duplicate one-time lines for one variant: keep the first key, sum the quantity, and let
      // the next write collapse them onto that line.
      this.#lines.set(id, {
        key: existing?.key ?? item.key,
        quantity: (existing?.quantity ?? 0) + item.quantity,
      });
    }
  }

  async #drain() {
    if (this.#draining) return;
    this.#draining = true;

    try {
      /** @type {Map<string, number>} */
      const attempts = new Map();

      while (this.#dirty.size) {
        // Inside the loop, not before it: a click landing mid-drain re-dirties a variant, and if
        // we had just created that variant's line with add.js we must know its key before writing
        // again — otherwise the second write is another add.js and the quantities compound.
        await this.#ensureLineKeys();

        const id = /** @type {string} */ (this.#dirty.values().next().value);
        this.#dirty.delete(id);

        const target = this.#desired.get(id) || 0;
        const line = this.#lines.get(id);
        if (line && line.quantity === target) continue;

        try {
          await this.#write(id, target, line);

          // A line key is NOT stable: it changes when the line's discount allocations change, so
          // crossing a savings tier silently invalidates every key we hold. A write addressed by a
          // stale key is accepted by Shopify and does nothing. Verify the write landed; if it
          // didn't, drop our keys, re-read, and try once more.
          const settled = this.#lines.get(id)?.quantity ?? 0;
          const tries = (attempts.get(id) ?? 0) + 1;
          attempts.set(id, tries);

          if (settled !== target && tries < 3) {
            this.#syncedKeys = false;
            this.#dirty.add(id);
          }
        } catch (error) {
          console.error('[collection-builder]', error);
          this.#revert(id);
          this.#showNotice("We couldn't update your bag. Please try again.");
          break;
        }
      }
    } finally {
      this.#draining = false;
    }

    // One authoritative read per burst, then one announcement.
    await this.#refresh();
  }

  async #write(id, target, line) {
    const body = line
      ? JSON.stringify({ id: line.key, quantity: target })
      : JSON.stringify({ items: [{ id: Number(id), quantity: target }] });

    const url = line ? Theme.routes.cart_change_url : Theme.routes.cart_add_url;
    const response = await fetch(url, fetchConfig('json', { body }));
    const payload = await response.json();

    if (!response.ok || payload.status || payload.errors) {
      throw new Error(payload.description || payload.message || 'Cart update failed');
    }

    if (line) {
      // change.js returns the whole cart.
      this.#adoptLines(payload);
    } else {
      // add.js returns only the lines it touched — enough to learn the new line's key so the NEXT
      // write for this variant is a change (absolute), not another add (cumulative).
      for (const item of payload.items ?? []) {
        if (item.selling_plan_allocation) continue;
        const addedId = String(item.variant_id);
        if (this.#cards.has(addedId)) this.#lines.set(addedId, { key: item.key, quantity: item.quantity });
      }
      // …but an add can cross a savings tier, which re-keys EVERY line, not just this one. We only
      // learned the touched line's key, so force a full re-read before writing any other variant.
      this.#syncedKeys = false;
    }
  }

  /** Pull the desired quantity for one variant back to whatever the cart actually holds. */
  #revert(id) {
    const truth = this.#lines.get(id)?.quantity ?? 0;
    if (truth > 0) this.#desired.set(id, truth);
    else this.#desired.delete(id);
    this.#render();
  }

  /** Section ids of every cart surface on the page — the same set `component-cart-items` collects. */
  #cartSectionIds() {
    const ids = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    }
    return Array.from(ids);
  }

  /**
   * One request that closes the burst: `/cart/update.js` with no updates is a read, but unlike
   * `/cart.js` it accepts `sections` — so we get authoritative cart state AND the re-rendered cart
   * sections together, exactly the payload shape every other cart mutation in the theme produces.
   * The event then carries `sections`, so `cart-items-component` morphs from it rather than firing
   * its own follow-up section fetch.
   */
  async #refresh() {
    let cart = null;
    const sectionIds = this.#cartSectionIds();

    try {
      const body = JSON.stringify({
        updates: {},
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      if (response.ok) cart = await response.json();
    } catch {
      return;
    }
    if (!cart) return;

    this.#syncedKeys = true;
    this.#adoptLines(cart);

    // Only adopt the cart's quantities if nothing is still pending, or we would stomp a click
    // the shopper made while this request was in flight.
    if (!this.#dirty.size && !this.#draining) this.#reconcile(cart, { skipAdopt: true });

    // The SAME `cart:update` every other cart mutation in the theme emits — one refresh
    // mechanism, not two. cart-items-component, cart-icon and savings-progress-pdp all pick this
    // up with no knowledge of the builder. `silent` suppresses only the drawer's AUTO-OPEN; the
    // drawer still refreshes its contents from the sections in this payload.
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.sectionId, {
        source: 'collection-builder',
        itemCount: cart.item_count,
        sections: cart.sections,
        silent: true,
      })
    );
  }

  /** Adopt an external cart as truth and repaint. */
  #reconcile(cart, { skipAdopt = false } = {}) {
    if (!cart?.items) return;
    if (!skipAdopt) this.#adoptLines(cart);

    this.#desired.clear();
    for (const [id, line] of this.#lines) {
      if (line.quantity > 0) this.#desired.set(id, line.quantity);
    }
    this.#render();
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  #render() {
    const total = this.#total;
    const atMax = this.max > 0 && total >= this.max;

    for (const [id, card] of this.#cards) {
      const qty = this.#desired.get(id) || 0;

      const qtyEl = card.querySelector('[data-cb-qty]');
      if (qtyEl) {
        qtyEl.textContent = String(qty);
        qtyEl.toggleAttribute('data-zero', qty === 0);
      }

      // Disabled, not hidden: the +/− pair is overlaid on the artwork and must hold its position.
      const minus = card.querySelector('[data-cb-step="-1"]');
      minus?.toggleAttribute('disabled', qty === 0);

      const plus = card.querySelector('[data-cb-step="1"]');
      if (plus instanceof HTMLElement) {
        const stock = Number(card.dataset.maxStock ?? -1);
        const stockBlocked = stock >= 0 && qty >= stock;
        plus.toggleAttribute('disabled', atMax || stockBlocked);
      }

      card.toggleAttribute('data-selected', qty > 0);
      card.dataset.quantity = String(qty);
    }

    // Keep the server-rendered attribute honest — it is the element's public state, and a stale
    // value misleads anything reading or styling off it.
    this.dataset.total = String(total);

    this.bar?.toggleAttribute('hidden', total === 0);
    this.#renderTray();

    // "4 Bars" / "In Your Bag" over two lines, title case — the unit label is stored title-cased
    // ("Bars", "Cylinders") precisely so it can be used verbatim here.
    if (this.countEl) {
      this.countEl.replaceChildren();
      if (total > 0) {
        const unit = this.#unitFor(total);
        const line1 = document.createElement('span');
        line1.className = 'cb__count-total';
        line1.textContent = unit ? `${total} ${unit}` : String(total);

        const line2 = document.createElement('span');
        line2.className = 'cb__count-suffix';
        line2.textContent = 'In Your Bag';

        this.countEl.append(line1, line2);
      }
    }

    if (atMax) this.#showNotice(`Maximum of ${this.max} reached.`);

    this.#announce(total === 0 ? 'Collection empty' : `${total} in your bag`);
  }

  /**
   * Each tray entry is a small product card: image, count chip overlapping its top-right corner,
   * and the variant's name beneath. The name is what makes the tray readable at a glance — a row
   * of thumbnails alone doesn't tell you what you picked.
   */
  #renderTray() {
    if (!this.tray) return;
    this.tray.replaceChildren();

    for (const [id, qty] of this.#desired) {
      const card = this.#cards.get(id);
      const name = card?.dataset.title ?? '';

      const cell = document.createElement('div');
      cell.className = 'cb__tray-item';
      cell.setAttribute('role', 'listitem');
      cell.title = `${name} ×${qty}`;

      const media = document.createElement('div');
      media.className = 'cb__tray-media';
      const thumb = card?.dataset.thumb;
      if (thumb) media.style.backgroundImage = `url('${thumb}')`;

      const badge = document.createElement('span');
      badge.className = 'cb__tray-badge';
      badge.textContent = String(qty);
      media.appendChild(badge);

      const label = document.createElement('span');
      label.className = 'cb__tray-label';
      label.textContent = name;

      cell.append(media, label);
      this.tray.appendChild(cell);
    }
  }

  #showNotice(text) {
    if (!this.notice) return;
    this.notice.textContent = text;
    this.notice.hidden = false;
  }

  #clearNotice() {
    if (!this.notice) return;
    this.notice.textContent = '';
    this.notice.hidden = true;
  }

  #announce(text) {
    if (this.status) this.status.textContent = text;
  }
}

if (!customElements.get('collection-builder')) {
  customElements.define('collection-builder', CollectionBuilder);
}
